import { Head, useForm, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, PieChart } from 'lucide-react'
import { FormEvent, useEffect, useState, useCallback } from 'react'

interface Commission {
  id: number
  agent_id: number
  carrier_id: number
  policy_number: string
  commission_type: string
  gross_amount: number
  agent_amount: number
  agent_split_percentage: number | null
  sponsor_split_percentage: number | null
  sponsor_amount: number | null
  period_start: string
  period_end: string
}

interface SplitTier {
  commission_number: number
  agent_percentage: number
  sponsor_percentage: number
}

interface SplitSchedule {
  tiers: SplitTier[]
  default_agent_percentage: number
  default_sponsor_percentage: number
}

interface AgentOption {
  id: number
  name: string
  parent_agent_id: number | null
  parent_agent_name: string | null
  commission_count: number
}

interface CarrierOption {
  id: number
  name: string
  carrier_payout_percentage: number
  commission_split_schedule: SplitSchedule
}

interface Props extends PageProps {
  commission: Commission | null
  agents: AgentOption[]
  carriers: CarrierOption[]
}

interface SplitResult {
  agentPct: number
  sponsorPct: number
  agentAmt: number
  sponsorAmt: number
  supplementAmt: number
  carrierPayoutPct: number
  commissionNumber: number
}

function calculateSplitLocally(
  agent: AgentOption | undefined,
  carrier: CarrierOption | undefined,
  grossAmount: number
): SplitResult | null {
  if (!agent || !carrier || grossAmount <= 0) return null

  const carrierPayoutPct = Number(carrier.carrier_payout_percentage ?? 100)
  const computeSupplement = (agentPct: number) => {
    const gap = agentPct - carrierPayoutPct
    if (gap <= 0) return 0
    return Math.round(grossAmount * gap) / 100
  }

  if (!agent.parent_agent_id) {
    return {
      agentPct: 100,
      sponsorPct: 0,
      agentAmt: grossAmount,
      sponsorAmt: 0,
      supplementAmt: computeSupplement(100),
      carrierPayoutPct,
      commissionNumber: agent.commission_count + 1
    }
  }

  const schedule = carrier.commission_split_schedule
  if (!schedule) return null

  const commissionNumber = agent.commission_count + 1
  const tiers = (schedule.tiers || []).sort((a: any, b: any) => Number(a.commission_number) - Number(b.commission_number))
  const matchingTier = tiers.find((t: any) => Number(t.commission_number) === commissionNumber)

  let agentPct: number, sponsorPct: number
  if (matchingTier) {
    agentPct = Number(matchingTier.agent_percentage)
    sponsorPct = Number(matchingTier.sponsor_percentage)
  } else {
    agentPct = Number(schedule.default_agent_percentage) || 90
    sponsorPct = Number(schedule.default_sponsor_percentage) || 10
  }

  const sponsorAmt = Math.round(grossAmount * sponsorPct) / 100
  const agentAmt = grossAmount - sponsorAmt

  return {
    agentPct,
    sponsorPct,
    agentAmt,
    sponsorAmt,
    supplementAmt: computeSupplement(agentPct),
    carrierPayoutPct,
    commissionNumber
  }
}

export default function CommissionForm({ commission, agents, carriers }: Props) {
  const isEdit = !!commission

  const { data, setData, post, put, processing } = useForm({
    agent_id: commission?.agent_id?.toString() || '',
    carrier_id: commission?.carrier_id?.toString() || '',
    policy_number: commission?.policy_number || '',
    commission_type: commission?.commission_type || 'first_year',
    gross_amount: commission?.gross_amount?.toString() || '',
    agent_amount: commission?.agent_amount?.toString() || '',
    agent_split_percentage: commission?.agent_split_percentage?.toString() || '',
    sponsor_split_percentage: commission?.sponsor_split_percentage?.toString() || '',
    sponsor_amount: commission?.sponsor_amount?.toString() || '',
    period_start: commission?.period_start || '',
    period_end: commission?.period_end || ''
  })

  const [splitPreview, setSplitPreview] = useState<SplitResult | null>(null)

  const selectedAgent = agents.find(a => a.id === Number(data.agent_id))
  const selectedCarrier = carriers.find(c => c.id === Number(data.carrier_id))
  const grossAmount = parseFloat(data.gross_amount) || 0

  const recalcSplit = useCallback(() => {
    if (!isEdit && selectedAgent && selectedCarrier && grossAmount > 0) {
      const result = calculateSplitLocally(selectedAgent, selectedCarrier, grossAmount)
      if (result) {
        setSplitPreview(result)
        setData((prev: any) => ({
          ...prev,
          agent_amount: result.agentAmt.toFixed(2),
          agent_split_percentage: result.agentPct.toString(),
          sponsor_split_percentage: result.sponsorPct.toString(),
          sponsor_amount: result.sponsorAmt.toFixed(2)
        }))
      }
    }
  }, [data.agent_id, data.carrier_id, data.gross_amount])

  useEffect(() => {
    recalcSplit()
  }, [recalcSplit])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (isEdit) {
      put(`/commissions/${commission.id}`)
    } else {
      post('/commissions')
    }
  }

  const formatCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`

  return (
    <AppLayout>
      <Head title={isEdit ? 'Edit Commission' : 'New Commission'} />

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/commissions" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
            {isEdit ? 'Edit Commission' : 'New Commission'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Agent *</label>
              <select
                value={data.agent_id}
                onChange={(e) => setData('agent_id', e.target.value)}
                className="input-field w-full"
                required
              >
                <option value="">Select agent...</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
              {selectedAgent && (
                <p className="text-xs text-chestnut-charcoal/60 mt-1">
                  {selectedAgent.parent_agent_name
                    ? `Sponsor: ${selectedAgent.parent_agent_name} | Prior commissions: ${selectedAgent.commission_count}`
                    : 'No sponsoring agent (receives 100%)'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Carrier *</label>
              <select
                value={data.carrier_id}
                onChange={(e) => setData('carrier_id', e.target.value)}
                className="input-field w-full"
                required
              >
                <option value="">Select carrier...</option>
                {carriers.map((carrier) => (
                  <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Policy Number *</label>
              <input
                type="text"
                value={data.policy_number}
                onChange={(e) => setData('policy_number', e.target.value)}
                className="input-field w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Commission Type *</label>
              <select
                value={data.commission_type}
                onChange={(e) => setData('commission_type', e.target.value)}
                className="input-field w-full"
              >
                <option value="first_year">First Year</option>
                <option value="renewal">Renewal</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Gross Commission Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-chestnut-charcoal/60">$</span>
              <input
                type="number"
                value={data.gross_amount}
                onChange={(e) => setData('gross_amount', e.target.value)}
                className="input-field w-full pl-7"
                step="0.01"
                min="0"
                required
              />
            </div>
          </div>

          {splitPreview && !isEdit && (
            <div className="bg-chestnut-cream/60 border border-chestnut-charcoal/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <PieChart className="h-5 w-5 text-chestnut-forest" />
                <h3 className="font-semibold text-chestnut-charcoal">Commission Split Preview</h3>
                <span className="text-xs text-chestnut-charcoal/60 ml-auto">Commission #{splitPreview.commissionNumber} for this agent</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-chestnut-charcoal/10">
                  <p className="text-xs text-chestnut-charcoal/60 mb-1">Writing Agent ({splitPreview.agentPct}%)</p>
                  <p className="text-lg font-bold text-chestnut-forest">{formatCurrency(splitPreview.agentAmt)}</p>
                  <p className="text-xs text-chestnut-charcoal/60 mt-1">{selectedAgent?.name}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-chestnut-charcoal/10">
                  <p className="text-xs text-chestnut-charcoal/60 mb-1">Sponsoring Agent ({splitPreview.sponsorPct}%)</p>
                  <p className="text-lg font-bold text-chestnut-gold">{formatCurrency(splitPreview.sponsorAmt)}</p>
                  <p className="text-xs text-chestnut-charcoal/60 mt-1">{selectedAgent?.parent_agent_name || 'N/A'}</p>
                </div>
              </div>

              <p className="text-xs text-chestnut-charcoal/50 mt-3">
                Total: {formatCurrency(splitPreview.agentAmt + splitPreview.sponsorAmt)} = {formatCurrency(grossAmount)} (must match)
              </p>

              {splitPreview.supplementAmt > 0 && (
                <div className="mt-4 rounded-lg border border-chestnut-gold/40 bg-white/70 p-3">
                  <p className="text-sm font-semibold text-chestnut-charcoal mb-1">Chestnut Carrier Supplement</p>
                  <p className="text-xs text-chestnut-charcoal/70 mb-2">
                    {selectedCarrier?.name} pays {splitPreview.carrierPayoutPct.toFixed(2)}% but the agent's
                    contracted rate is {splitPreview.agentPct.toFixed(2)}%. Chestnut covers the gap.
                  </p>
                  <p className="text-base font-bold text-chestnut-gold">
                    Supplement: {formatCurrency(splitPreview.supplementAmt)}
                  </p>
                </div>
              )}
            </div>
          )}

          {isEdit && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Agent Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-chestnut-charcoal/60">$</span>
                  <input
                    type="number"
                    value={data.agent_amount}
                    onChange={(e) => {
                      const agentAmt = parseFloat(e.target.value) || 0
                      const gross = parseFloat(data.gross_amount) || 0
                      const sponsorAmt = Math.max(0, gross - agentAmt)
                      setData((prev: any) => ({
                        ...prev,
                        agent_amount: e.target.value,
                        sponsor_amount: sponsorAmt.toFixed(2),
                        agent_split_percentage: gross > 0 ? ((agentAmt / gross) * 100).toFixed(2) : '0',
                        sponsor_split_percentage: gross > 0 ? ((sponsorAmt / gross) * 100).toFixed(2) : '0'
                      }))
                    }}
                    className="input-field w-full pl-7"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Sponsor Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-chestnut-charcoal/60">$</span>
                  <input
                    type="number"
                    value={data.sponsor_amount}
                    onChange={(e) => {
                      const sponsorAmt = parseFloat(e.target.value) || 0
                      const gross = parseFloat(data.gross_amount) || 0
                      const agentAmt = Math.max(0, gross - sponsorAmt)
                      setData((prev: any) => ({
                        ...prev,
                        sponsor_amount: e.target.value,
                        agent_amount: agentAmt.toFixed(2),
                        agent_split_percentage: gross > 0 ? ((agentAmt / gross) * 100).toFixed(2) : '0',
                        sponsor_split_percentage: gross > 0 ? ((sponsorAmt / gross) * 100).toFixed(2) : '0'
                      }))
                    }}
                    className="input-field w-full pl-7"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Period Start</label>
              <input
                type="date"
                value={data.period_start}
                onChange={(e) => setData('period_start', e.target.value)}
                className="input-field w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Period End</label>
              <input
                type="date"
                value={data.period_end}
                onChange={(e) => setData('period_end', e.target.value)}
                className="input-field w-full"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-chestnut-gray">
            <Link href="/commissions" className="btn-secondary">Cancel</Link>
            <button type="submit" disabled={processing} className="btn-primary">
              {processing ? 'Saving...' : (isEdit ? 'Update Commission' : 'Create Commission')}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
